import { readStored, writeStored } from '@/lib/storage'
import { apiUrl, type HttpResponse, httpJson, httpStatus, NETWORK_TIMEOUT_MS, now, withTimeout } from '@/lib/compat'

/**
 * TV sessiya saxlanışı — QR ilə giriş nəticəsində alınan tokenlər.
 *
 * Vebdəki `apps/web/src/lib/api.ts` ilə eyni müqavilə (AuthResultDto): token
 * bitəndə `/api/auth/refresh` ilə yenilənir, alınmasa sessiya təmizlənir və
 * istifadəçi QR ilə yenidən bağlanır.
 */

export interface TvSession {
  userId: string
  email: string | null
  fullName: string | null
  languageCode: string
  accessToken: string
  accessTokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
  roles: string[]
}

const SESSION_KEY = 'kb_tv_session'
const SESSION_CONTEXT_KEY = 'kb_tv_session_context'
let sessionEpoch = 0
let observedContext: string | null = null

/** Giriş/çıxış sərhədi — token yenilənməsi eyni hesabın əməlini ləğv etmir. */
export function sessionGeneration(): number {
  // 2026-10-13: başqa tabda eyni hesaba giriş zamanı modul sayğacı dəyişmirdi;
  // gecikmiş şəxsi cavab yeni sessiyaya keçirdi. Rotasiya bu möhürü dəyişmir.
  const current = readStored(SESSION_CONTEXT_KEY)
  if (current !== observedContext) {
    observedContext = current
    sessionEpoch += 1
  }
  return sessionEpoch
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GİRİŞ REJİMİ
 * ═══════════════════════════════════════════════════════════════════════════
 *   'mock' (DEFAULT) — app SANKİ GİRİŞ EDİLMİŞ kimi açılır. QR ekranı yenə
 *                      əlçatandır (Profil → «Cihazı yenidən bağla») və orada
 *                      REAL, skan oluna bilən QR göstərilir — sadəcə təsdiq
 *                      telefon əvəzinə taymerlə gəlir. Backend işləməsə də
 *                      bütün 10-foot UI-ı gəzmək olur.
 *   'real'           — POST /api/auth/qr/{start,poll} axını (backend hazırdır,
 *                      V020 migrasiyası tətbiq olunmalıdır).
 *
 * Dəyişmək üçün `.env` faylında: `VITE_KB_AUTH=real`
 * ═══════════════════════════════════════════════════════════════════════════
 */
export type AuthMode = 'mock' | 'real'

// ⚠ MOCK LOGİN LƏĞV EDİLDİ (istifadəçi tələbi 2026-07-28) — TV həmişə REAL QR girişi işlədir.
// `VITE_KB_AUTH=mock` ilə də mock AÇILMIR: giriş yalnız telefonla QR təsdiqi ilə olur (backend V020).
// (Bütün `AUTH_MODE === 'mock'` budaqları artıq ölüdür — sənədləşmə/badge kimi qalır, icra olunmur.)
export const AUTH_MODE: AuthMode = 'real'

/** Mock rejimin nümayiş sessiyası. */
const MOCK_SESSION: TvSession = {
  userId: '00000000-0000-0000-0000-000000000001',
  email: 'demo@yeni.kinobox.az',
  fullName: 'Demo İstifadəçi',
  languageCode: 'az',
  accessToken: 'mock-access-token',
  accessTokenExpiresAt: '2099-01-01T00:00:00Z',
  refreshToken: 'mock-refresh-token',
  refreshTokenExpiresAt: '2099-01-01T00:00:00Z',
  roles: ['user'],
}

/** Cihazda saxlanmış sessiyanı (token cütü) oxuyur. */
export function readSession(): TvSession | null {
  if (AUTH_MODE === 'mock') return MOCK_SESSION
  try {
    const raw = readStored(SESSION_KEY)
    return raw ? (JSON.parse(raw) as TvSession) : null
  } catch {
    return null
  }
}

/** Sessiyanı cihaza yazır. */
export function writeSession(session: TvSession | null): void {
  sessionEpoch += 1
  observedContext = Date.now() + ':' + Math.random()
  writeStored(SESSION_CONTEXT_KEY, observedContext)
  persistSession(session)
}

function persistSession(session: TvSession | null): void {
  writeStored(SESSION_KEY, session ? JSON.stringify(session) : null)
}

/** Cari cihazın sessiyasını ləğv edir; çıxış şəbəkəni gözləmir. */
export function signOut(): void {
  const captured = readSession()
  writeSession(null)
  // 2026-10-04: TV çıxışı yalnız saxlancı silirdi, serverdə refresh token qalırdı.
  // Köhnə token cütü ayrıca saxlanır: yeni hesabın sessiyasına toxunulmur.
  if (captured && captured.refreshToken) void revokeCapturedSession(captured).catch(() => undefined)
}

function revokeCapturedSession(captured: TvSession): Promise<void> {
  const revoke = (session: TvSession) => sendWithToken('/api/auth/logout', {
    method: 'POST', body: JSON.stringify({ refreshToken: session.refreshToken, allDevices: false }),
  }, session.accessToken)
  return revoke(captured).then((response) => {
    if (response.status !== 401) return
    // Bitmiş access token yalnız çıxarılan hesab üçün yenilənir. Bu cavab cari
    // sessiyaya yazılmır və authStatus yeni hesabla sorğunu təkrarlamır.
    return sendRefresh(captured.refreshToken).then((refreshed) => {
      if (refreshed.status !== 200) return
      const isolated = JSON.parse(refreshed.text) as TvSession
      if (!isolated || isolated.userId !== captured.userId || !isolated.accessToken || !isolated.refreshToken) return
      return revoke(isolated).then(() => undefined)
    })
  })
}

// ── Access token yeniləmə (refresh rotation) ───────────────────────────────

/**
 * ⚠ NİYƏ ƏLAVƏ OLUNDU (2026-07-31) — «treyler açılır, film açılmır» QÜSURUNUN KÖK SƏBƏBİ.
 *
 * TV app-ında refresh axını HEÇ VAXT YAZILMAMIŞDI. Faylın başındakı şərh «token
 * bitəndə yenilənir» desə də, `/api/auth/refresh`-i çağıran BİR SƏTİR DƏ yox idi;
 * `accessTokenExpiresAt` heç yerdə oxunmurdu. Access token ömrü isə cəmi
 * **15 DƏQİQƏDİR** (`Api/src/Kinobox.Api/appsettings.json` → `Jwt:AccessTokenMinutes`),
 * refresh token 30 gün.
 *
 * Nəticə — cütləşdirmədən 15 dəqiqə sonra AUTH tələb edən HƏR sorğu 401 alırdı:
 *   · `POST /api/playback/ticket`      → film açılmır («Daxil olun» / «hazır deyil»)
 *   · `POST /api/playback/live/ticket` → canlı açılmır
 *   · `GET  /api/me/profiles`          → profil siyahısı BOŞ qayıdır
 * Treyler isə ANONİM uçdan (`/api/catalog/content/{slug}/trailer`) gəldiyi üçün
 * işləməyə davam edirdi — istifadəçinin gördüyü təzahür məhz budur: treyler oynayır,
 * film oynamır. «Cihazı yenidən bağlayanda» yeni token alınır və növbəti 15 dəqiqə
 * işləyir; bu da simptomun ikinci yarısını izah edir.
 *
 * Axın vebdəki `apps/web/src/lib/api.ts` ilə eynidir: tək uçuşlu (single-flight)
 * refresh + 401-də bir dəfə təkrar. Fərq — TV-də `fetch` olmaya bilər, ona görə
 * `httpStatus` (fetch/XHR cütü) işlədilir.
 */

/** Paralel 401-lər TƏK refresh sorğusuna yığılsın deyə (rotation-da yarış olmasın). */
let refreshInFlight: Promise<TvSession | null> | null = null
let refreshingToken: string | null = null

/**
 * Token-i yeniləyir. Uğurda yeni sessiya yazılır və qaytarılır.
 *
 * ⚠ SESSİYA YALNIZ SERVER «yox» DEYƏNDƏ təmizlənir (401/403 — refresh token 30 günü
 *   doldurub və ya ləğv olunub). ŞƏBƏKƏ xətasında sessiya SAXLANILIR: TV-də qısa
 *   şəbəkə kəsintisi istifadəçini sistemdən çıxarmamalıdır (yenidən QR skan etmək
 *   pultla ağır işdir).
 */
export function refreshSession(): Promise<TvSession | null> {
  const current = readSession()
  if (!current || !current.refreshToken) return Promise.resolve<TvSession | null>(null)
  if (refreshInFlight && refreshingToken === current.refreshToken) return refreshInFlight

  refreshingToken = current.refreshToken
  const asked = sessionGeneration()
  refreshInFlight = sendRefresh(current.refreshToken)
    .then((response): TvSession | null => {
      // 2026-10-04: gecikmiş refresh çıxışdan sonra sessiyanı bərpa etməməlidir.
      const latest = readSession()
      if (asked !== sessionGeneration() || (latest ? latest.refreshToken : undefined) !== current.refreshToken) return null
      if (response.status < 200 || response.status >= 300) {
        // 2026-10-24: 400 də qətidir (boş/korlanmış token — eyni token həmişə 400 alır); saytda
        // bu boşluq ölü sessiyanın dayanmadan 401 yığmasına səbəb olurdu (`apps/web` api.ts).
        if (response.status === 400 || response.status === 401 || response.status === 403) writeSession(null)
        return null
      }
      try {
        const next = JSON.parse(response.text) as TvSession
        if (!next || !next.accessToken) return null
        persistSession(next)
        return next
      } catch {
        return null
      }
    })
    .catch((): TvSession | null => null)
    .then((result) => {
      if (refreshingToken === current.refreshToken) {
        refreshInFlight = null
        refreshingToken = null
      }
      return result
    })

  return refreshInFlight
}

/**
 * Refresh sorğusunun ÖZÜ — şəbəkə xətasında BİR DƏFƏ təkrarlanır.
 *
 * ⚠ NİYƏ TƏHLÜKƏSİZDİR (API müqaviləsi 11, 2026-08-01): server rotasiyadan sonra
 *   **60 saniyəlik** pəncərədə köhnə refresh token-in təkrarını artıq oğurluq
 *   saymır — 200 + EYNİ `refreshToken` qaytarır və sessiyaları ÖLDÜRMÜR
 *   (`Jwt:RefreshReuseLeewaySeconds`). Yəni sorğu bu pəncərədə İDEMPOTENTDİR.
 *   Əvvəl təkrar cəhd təhlükəli idi: cavab yolda itsəydi (TV-də Wi-Fi yuxudan
 *   qalxanda tipik haldır) ikinci cəhd BÜTÜN sessiyaları bağlayırdı və istifadəçi
 *   pultla yenidən QR skan etməli olurdu.
 *
 * ⚠ TIMEOUT-DAN SONRA TƏKRAR ETMİRİK. Sorğu 20 saniyə asılı qalıbsa şəbəkə
 *   həqiqətən ölüdür; ikinci 20 saniyə gözləmək ekranı iki qat uzun «Yüklənir…»-də
 *   saxlayardı — istifadəçi bunu donma sayır (PERFORMANCE.md §6). Ona görə yalnız
 *   TEZ sınan cəhd (bağlantı rədd edildi · DNS · Wi-Fi oyanışı) təkrarlanır.
 *
 * Statuslu cavab (401/403/5xx) təkrarlanmır — server danışır, cavabı çağıran oxuyur.
 */
const REFRESH_RETRY_DELAY_MS = 800

function sendRefresh(refreshToken: string): Promise<HttpResponse> {
  const attempt = () =>
    withTimeout(
      httpStatus('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
      NETWORK_TIMEOUT_MS,
      'auth/refresh',
    )

  const startedAt = now()
  return attempt().catch((error: unknown) => {
    if (now() - startedAt >= NETWORK_TIMEOUT_MS) throw error instanceof Error ? error : new Error(String(error))
    return delay(REFRESH_RETRY_DELAY_MS).then(attempt)
  })
}

function delay(ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/** Token bitməyə bu qədər qalıbsa qabaqcadan yenilə (saat sürüşməsi payı ilə). */
const REFRESH_SKEW_MS = 60000

/**
 * Etibarlı access token qaytarır — lazım olsa qabaqcadan yeniləyir.
 * `null` = sessiya yoxdur və ya refresh alınmadı → çağıran «daxil olun»a düşür.
 */
export function validAccessToken(): Promise<string | null> {
  const session = readSession()
  if (!session || !session.accessToken) return Promise.resolve<string | null>(null)

  // ⚠ `Date.parse` köhnə TV mühərrikində .NET-in 7 rəqəmli kəsr saniyəsini
  //   həll etməyə bilər → NaN. Onda PROAKTİV yeniləmə etmirik; 401 gələndə
  //   aşağıdakı reaktiv yol onsuz da işləyir (davranış pisləşmir).
  const expiresAt = Date.parse(session.accessTokenExpiresAt)
  if (isNaN(expiresAt) || expiresAt - Date.now() > REFRESH_SKEW_MS) {
    return Promise.resolve<string | null>(session.accessToken)
  }

  return refreshSession().then((next) => (next ? next.accessToken : null))
}

/**
 * AUTH tələb edən sorğular üçün TƏK GİRİŞ NÖQTƏSİ — `Bearer` əlavə edir,
 * token bitmək üzrədirsə qabaqcadan yeniləyir, 401 gəlsə bir dəfə yeniləyib təkrarlayır.
 *
 * Status kodu ATILMIR: çağıranlar üçün 401 (giriş) · 403 (paywall) · 404 (yoxdur)
 * fərqi mənalıdır. Əlavə olaraq **0 = şəbəkə** — bax `refreshFailure`.
 */
export function authStatus(
  path: string,
  init?: { method?: string; body?: string; timeoutMs?: number },
): Promise<HttpResponse> {
  // 2026-10-23: `?.` yoxdur (ES2015 həddi, Frontend/CLAUDE.md §2.1).
  const userOf = (): string | undefined => {
    const session = readSession()
    return session ? session.userId : undefined
  }
  const requestUser = userOf()
  const requestEpoch = sessionGeneration()
  const isCurrent = () => requestEpoch === sessionGeneration() && userOf() === requestUser
  // 2026-10-04: regressiya testində köhnə 200 cavabı başqa və təkrar daxil olmuş
  // eyni hesaba keçirdi. Möhür hər cavabda yoxlanır; refresh möhürü dəyişmir.
  const ownedResponse = (response: HttpResponse): HttpResponse =>
    isCurrent() ? response : { status: 401, text: '' }
  return validAccessToken().then((token) => {
    if (!isCurrent()) return { status: 401, text: '' }
    if (!token) return refreshFailure({ status: 401, text: '' })
    return sendWithToken(path, init, token).then((response) => {
      if (!isCurrent()) return { status: 401, text: '' }
      if (response.status !== 401) return response
      const current = readSession()
      if (!current || current.userId !== requestUser) return response
      if (current.accessToken !== token) return sendWithToken(path, init, current.accessToken).then(ownedResponse)
      return refreshSession().then((next) => {
        if (!isCurrent()) return { status: 401, text: '' }
        return next ? sendWithToken(path, init, next.accessToken).then(ownedResponse) : refreshFailure(response)
      })
    })
  })
}

/**
 * SƏHİFƏ GİZLƏNƏRKƏN/BAĞLANARKƏN göndərilən son sorğu (`pagehide` · `visibilitychange`) —
 * saytdakı `keepalive` yolu (`apps/web/src/lib/playback.ts`).
 *
 * 2026-10-25, kod oxunuşu: oynadıcıda bu hadisələrin heç biri dinlənmirdi — tab/app bağlananda
 * son 14 saniyənin mövqeyi itirdi, baxış sessiyası isə 180 saniyə «canlı» qalıb ekran yeri
 * tuturdu. XHR (`httpStatus`) səhifə bağlananda ləğv olunur; `fetch(..., { keepalive })` isə
 * səhifədən sonra da gedir. `sendBeacon` yaramır — `Authorization` başlığı qoya bilmir.
 * ⚠ TOKEN YENİLƏNMİR: bağlanan səhifə refresh cavabını gözləyə bilməz. Token bitibsə və ya
 *   `fetch` yoxdursa (Chrome 42-dən köhnə) adi `authStatus` yoluna düşülür — gizlənən (amma
 *   bağlanmayan) səhifədə o da sona çatır. Nəticə gözlənilmir, hər halda best effort-dur.
 */
export function sendKeepalive(path: string, init: { method: string; body?: string }): void {
  const session = readSession()
  if (!session || !session.accessToken) return
  const expiresAt = Date.parse(session.accessTokenExpiresAt)
  const fresh = isNaN(expiresAt) || expiresAt - Date.now() > REFRESH_SKEW_MS
  if (!fresh || typeof fetch !== 'function') {
    void authStatus(path, init).catch(() => undefined)
    return
  }
  const headers: Record<string, string> = {
    authorization: 'Bearer ' + session.accessToken,
    'x-kinobox-surface': 'webtv',
  }
  if (init.body) headers['content-type'] = 'application/json'
  try {
    void fetch(apiUrl(path), { method: init.method, body: init.body, headers, keepalive: true }).catch(
      () => undefined,
    )
  } catch {
    // Köhnə mühərrik `keepalive` seçimini rədd edə bilər — adi yol.
    void authStatus(path, init).catch(() => undefined)
  }
}

/**
 * Yeniləmə ALINMAYANDA çağırana nə qaytarılsın.
 *
 * ⚠ NİYƏ LAZIM OLDU (2026-07-31, brauzerdə ölçülüb). `refreshSession()` `null`
 *   qaytarmağın İKİ tamam fərqli səbəbi var və əvvəl hər ikisi 401-ə çevrilirdi:
 *     · server «yox» dedi (refresh 401/403) → sessiya artıq təmizlənib, İSTİFADƏÇİ
 *       həqiqətən çıxıb → 401 doğrudur;
 *     · KEÇİCİ nasazlıq (şəbəkə kəsintisi · 429 rate limit · 500/502 · timeout) →
 *       sessiya YERİNDƏDİR, amma ekranda «Daxil olun — QR ilə daxil olun» yazılırdı.
 *   İkinci hal TV-də ağır zərərdir: pultla yenidən QR cütləşdirmək uzun əməliyyatdır,
 *   halbuki 2 saniyəlik Wi-Fi kəsintisindən sonra sadəcə təkrar cəhd kifayət edir.
 *   Ölçmə: `page.route('**​/api/auth/refresh', abort)` + bitmiş access token →
 *   sessiya `localStorage`-da qalır, ekran isə «Daxil olun» deyirdi.
 *
 * Ona görə keçici nasazlıqda **0 (şəbəkə)** qaytarılır: `playback.ts`/`live.ts`
 * bunu `unavailable` («Oynatma hazır deyil»), `profiles.ts` isə `error`
 * («Profillər alınmadı») kimi oxuyur — heç biri istifadəçini QR-a göndərmir.
 */
function refreshFailure(fallback: HttpResponse): HttpResponse {
  const session = readSession()
  return session && session.refreshToken ? { status: 0, text: '' } : fallback
}

function sendWithToken(
  path: string,
  init: { method?: string; body?: string; timeoutMs?: number } | undefined,
  token: string,
): Promise<HttpResponse> {
  return httpStatus(path, {
    method: (init && init.method) || 'GET',
    body: init && init.body,
    headers: { authorization: 'Bearer ' + token },
    // ⚠ Yalnız long-poll üçün (bax `compat.ts` → `timeoutMs`); boş buraxılanda
    //   ümumi şəbəkə həddi işləyir.
    timeoutMs: init && init.timeoutMs,
  })
}

// ── QR cütləşdirmə (device pairing) ────────────────────────────────────────

/** QR seansının başlanğıc cavabı — ekranda göstəriləcək kod və poll token-i. */
export interface QrStart {
  sessionId: string
  code: string
  verificationUrl: string
  pollToken: string
  expiresAt: string
}

// ⚠ Yalnız MOCK rejim (lokal dev) üçün göstərilən ünvan. Real rejimdə URL backend-dən
//    gəlir (site.public_url/env). 1 oktyabr 2026-dan sayt `kinobox.az`-dadır.
const DEFAULT_VERIFICATION_URL = 'https://kinobox.az/link'

/** Mock rejimdə kod göstəriləndən neçə sonra «telefon təsdiqlədi» sayılsın. */
const MOCK_APPROVE_AFTER_MS = 6000
let mockStartedAt = 0

/**
 * Cütləşdirmə sessiyası açır.
 *
 * Mock rejimdə kod LOKAL yaradılır. Format backend-dəki
 * `ITokenGenerator.GenerateShortCode()` ilə eynidir: ABCD-EFGH, alfabetdə
 * qarışdırılan simvollar (0/O, 1/I/L) yoxdur — pultla oxuyan üçün vacibdir.
 */
export function startQrLogin(): Promise<QrStart> {
  if (AUTH_MODE === 'mock') {
    const code = mockCode()
    // ⚠ Geri sayım KOD EKRANA ÇIXAN AN başlayır, ilk poll-da yox. Əks halda
    // ilk 3 saniyəlik poll intervalı da üstünə gəlir və «6 saniyə» əslində
    // 9 saniyə olur — ekrandakı nişanla uyğunsuzluq yaranır.
    mockStartedAt = Date.now()
    return Promise.resolve({
      sessionId: 'mock-session',
      code,
      verificationUrl: DEFAULT_VERIFICATION_URL + '?code=' + code,
      pollToken: 'mock-poll-token',
      expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
    })
  }

  return withTimeout(
    httpJson<QrStart>('/api/auth/qr/start', {
      method: 'POST',
      body: JSON.stringify({ deviceName: deviceLabel() }),
    }),
    NETWORK_TIMEOUT_MS,
    'qr/start',
  )
}

/** QR seansının vəziyyət cavabı. */
export type PollResult =
  | { status: 'pending' }
  | { status: 'expired' }
  | { status: 'ok'; session: TvSession }

/**
 * TV seansın təsdiqlənib-təsdiqlənmədiyini soruşur.
 * ⚠ Poll token ekrandakı KODDAN AYRIDIR: kod görünür, token yox.
 */
export function pollQrLogin(pollToken: string): Promise<PollResult> {
  if (AUTH_MODE === 'mock') {
    if (mockStartedAt === 0 || Date.now() - mockStartedAt < MOCK_APPROVE_AFTER_MS) {
      return Promise.resolve<PollResult>({ status: 'pending' })
    }
    mockStartedAt = 0
    return Promise.resolve<PollResult>({ status: 'ok', session: MOCK_SESSION })
  }

  // ⚠ 202 = gözləyir · 200 = token · 410 = vaxtı keçib. Status kodları məlumat
  //   daşıyır, ona görə `httpJson` (status-u atır) yerinə `httpStatus` işlədilir.
  //
  // ⚠ ƏVVƏL BURADA BİRBAŞA `fetch` VAR İDİ və bu, TV-də sınırdı: `fetch` OLMAYAN
  //   brauzerdə (köhnə Tizen/webOS — Chrome 38-47) ifadə sorğu getməmişdən əvvəl
  //   `ReferenceError` atırdı. `httpStatus` `fetch` yoxdursa XHR-ə düşür.
  return withTimeout(
    httpStatus('/api/auth/qr/poll', {
      method: 'POST',
      body: JSON.stringify({ pollToken }),
    }).then((response): PollResult => {
      if (response.status === 202) return { status: 'pending' }
      if (response.status === 400 || response.status === 401 || response.status === 403 || response.status === 410) return { status: 'expired' }
      // 2026-10-04: 429/5xx etibarlı QR kodunu dəyişib telefondakı təsdiqi itirirdi.
      // Keçici nasazlıqda ekran eyni poll token ilə növbəti cəhdi planlaşdırır.
      if (response.status !== 200) throw new Error('qr/poll: HTTP ' + response.status)
      return { status: 'ok', session: JSON.parse(response.text) as TvSession }
    }),
    NETWORK_TIMEOUT_MS,
    'qr/poll',
  )
}

function mockCode(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 8; i++) {
    if (i === 4) out += '-'
    out += alphabet.charAt(Math.floor(Math.random() * alphabet.length))
  }
  return out
}

/**
 * Təsdiq ekranında istifadəçi hansı cihazı bağladığını görsün.
 *
 * ⚠⚠ QAYTARILAN DƏYƏR TƏRCÜMƏ OLUNMUR VƏ OLUNMAMALIDIR (2026-08-30). O, iki yerə
 *    gedir: (1) ekrandakı «Cihaz» sətri, (2) `/api/auth/qr/start` gövdəsindəki
 *    `deviceName`. İkincisi SERVERDƏ SAXLANILIR və telefonda/saytda «cihazlarım»
 *    siyahısında görünür — burada tərcümə etsək eyni televizor TV-dəki dil
 *    seçiminə görə hər dəfə başqa adla qeyd olunardı və istifadəçi siyahıda
 *    «Televizor», «Телевизор», «Televizyon» sətirlərini yan-yana görərdi.
 * ⚠ ONA GÖRƏ SARĞI ÇAĞIRIŞ YERİNDƏDİR: `screens/profile.ts` və
 *   `browser/screens/account.ts` dəyəri `T()`-dən keçirir; lüğətdə yalnız
 *   «Televizor» açarı var (brend adları tərcümə olunmur).
 */
export function deviceLabel(): string {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (/tizen/i.test(agent)) return 'Samsung TV'
  if (/web0s|webos/i.test(agent)) return 'LG TV'
  return 'Televizor'
}
